// Directional reference data for the branded "Vastu Placement & Devtas" report.
// Effects are cleaned, professional-English interpretations of the consultant's master sheet.
// 16 directions in clockwise order starting from North.

export const DIRECTIONS = [
  { code: "N", name: "North", element: "Water", color: "#2563EB" },
  { code: "NNE", name: "North-North-East", element: "Water", color: "#2563EB" },
  { code: "NE", name: "North-East", element: "Water", color: "#2563EB" },
  { code: "ENE", name: "East-North-East", element: "Air / Wood", color: "#16A34A" },
  { code: "E", name: "East", element: "Air / Wood", color: "#16A34A" },
  { code: "ESE", name: "East-South-East", element: "Air / Wood", color: "#16A34A" },
  { code: "SE", name: "South-East", element: "Fire", color: "#DC2626" },
  { code: "SSE", name: "South-South-East", element: "Fire", color: "#DC2626" },
  { code: "S", name: "South", element: "Fire", color: "#DC2626" },
  { code: "SSW", name: "South-South-West", element: "Earth", color: "#CA8A04" },
  { code: "SW", name: "South-West", element: "Earth", color: "#CA8A04" },
  { code: "WSW", name: "West-South-West", element: "Space", color: "#64748B" },
  { code: "W", name: "West", element: "Space", color: "#64748B" },
  { code: "WNW", name: "West-North-West", element: "Space", color: "#64748B" },
  { code: "NW", name: "North-West", element: "Space", color: "#64748B" },
  { code: "NNW", name: "North-North-West", element: "Water", color: "#2563EB" },
];

const DIR_META = Object.fromEntries(DIRECTIONS.map((d) => [d.code, d]));

// Each room type → map of direction code → cleaned professional effect.
// "—" denotes a placement not meaningfully defined in the source sheet.
const EFFECTS = {
  Toilet: {
    N: "Flushes away money and financial opportunities.",
    NNE: "Flushes away health and vitality.",
    NE: "Flushes away fortune and divine grace — a serious defect.",
    ENE: "Flushes away joy and recreation.",
    E: "Flushes away social standing, discipline and health.",
    ESE: "Flushes away clear, focused thinking.",
    SE: "Flushes away money and the health of the women of the house.",
    SSE: "Flushes away power and confidence.",
    S: "Flushes away name, fame and relaxation.",
    SSW: "Very favourable location for a toilet.",
    SW: "Flushes away relationships and skills.",
    WSW: "Flushes away savings.",
    W: "Flushes away profit and gains.",
    WNW: "Helps flush away depression and anxiety — a useful placement.",
    NW: "Flushes away support and friendships.",
    NNW: "Flushes away intimacy and attraction.",
  },
  Kitchen: {
    N: "Drains wealth; stalls promotions and opportunities — best avoided.",
    NNE: "Burns away good health; can trigger migraines and chronic ailments.",
    NE: "Destroys fortune and can cause headaches — strictly avoid.",
    ENE: "Supports fun and recreation.",
    E: "Acceptable (50-50); works from East toward South-East, the direction of the Sun.",
    ESE: "Promotes thoughtfulness; suits analysts and planners.",
    SE: "Ideal — the fire zone; boosts power and confidence.",
    SSE: "May cause arguments and aggression.",
    S: "Recommended — ruled by Mars, performs very well here.",
    SSW: "Keeps conversations centred on expenses.",
    SW: "Can erode relationships and skills and bring instability — avoid.",
    WSW: "Keeps conversations centred on savings.",
    W: "Burns away profit and gains.",
    WNW: "Breeds negativity and discontent; guests feel unwelcome.",
    NW: "Workable; also a good spot for a utility / washing area.",
    NNW: "Keeps talk centred on intimacy and gossip.",
  },
  Bedroom: {
    N: "Can cause one partner to drift away or remain absent.",
    NNE: "—",
    NE: "Not ideal; can cause one partner to drift away.",
    ENE: "Quite favourable in the eastern sector.",
    E: "Quite favourable in the eastern sector.",
    ESE: "—",
    SE: "Causes friction and quarrels; only suitable for those above 70.",
    SSE: "—",
    S: "A good, restful location for a bedroom.",
    SSW: "—",
    SW: "Ideal master bedroom for the head of the family — ruled by ancestors and Indra-Jayant.",
    WSW: "—",
    W: "Slows you down; good for deep, restful sleep.",
    WNW: "—",
    NW: "Air zone — risk of joint pain and vata (rheumatic) ailments; a major concern.",
    NNW: "Favourable for couples — enhances intimacy and attraction.",
  },
  Temple: {
    N: "Attracts wealth and financial gains.",
    NNE: "Health-related worship here is not favourable.",
    NE: "Highly auspicious — fortune, divine blessings and prosperity. The best zone for a temple.",
    ENE: "Brings enjoyment and togetherness.",
    E: "Strengthens social connections and health.",
    ESE: "—",
    SE: "Good cash flow but mixed with anger; also brings comfort and luxury.",
    SSE: "Governs power, confidence, conflict and decisive action.",
    S: "Associated with Yama — can bring accidents and financial loss; avoid.",
    SSW: "Tends to increase expenses.",
    SW: "Suppresses manifestation; may bring sudden mishaps and losses.",
    WSW: "Tends to erode savings.",
    W: "Delays outcomes and manifestations.",
    WNW: "Helps dissolve depression.",
    NW: "Restless, excessive flow with disputes and higher liabilities.",
    NNW: "—",
  },
  "Drawing Room": {
    N: "Encourages conversations about money and sound investment decisions.",
    NNE: "—",
    NE: "Excellent — fosters spiritual and meaningful conversations.",
    ENE: "Best direction for a drawing room.",
    E: "Best direction for a drawing room.",
    ESE: "Encourages over-thinking and worry.",
    SE: "Leads to arguments and unnecessary conflict.",
    SSE: "Favourable location.",
    S: "Relaxed conversations about life and well-being.",
    SSW: "Creates expenses and strains relationships; place a thick yellow cord under the mattress as a remedy.",
    SW: "Family gatherings and kitty parties; still considered a negative placement.",
    WSW: "Direction of education; ideal seating for children — favourable.",
    W: "Good for dining; conversations about money and profit happen here.",
    WNW: "Strong risk of psychological problems — a major defect.",
    NW: "Encourages big, unrealistic dreams but strengthens friendships.",
    NNW: "Good for couples and intimacy — better suited as a bedroom.",
  },
  "TV / Mobile": {
    N: "Draws attention toward money-related channels and content.",
    NNE: "Harms health; linked to headaches and neurological strain.",
    NE: "Keeps the mind absorbed in sermons and religious content around the clock.",
    ENE: "Drains the fun and happiness from daily life.",
    E: "Keeps news and current affairs in constant play.",
    ESE: "Attracts tense, worry-driven and domestic-drama content.",
    SE: "Breeds conflict and aggression.",
    SSE: "Not favourable.",
    S: "Attracts mysterious content.",
    SSW: "Fuels impulsive spending and online shopping.",
    SW: "Mystery and family serials watched together.",
    WSW: "Attracts savings and finance content (business news).",
    W: "Keeps questioning where profit will come from.",
    WNW: "Attracts gloomy, depressing content.",
    NW: "Attracts travel content and blogs.",
    NNW: "Pulls toward adult / romantic content — a TV is best avoided here.",
  },
  "Store / Stair": {
    N: "Blocks income and opportunities; promotions and new openings dry up.",
    NNE: "Weakens interest in yoga, Ayurveda and well-being.",
    NE: "Delays luck and good fortune.",
    ENE: "Dampens fun and lightheartedness.",
    E: "Weakens social connections and limits public reach.",
    ESE: "Dulls thinking and analysis.",
    SE: "Causes health issues and disturbs cash flow and vitality.",
    SSE: "Drains energy, vigour and vitality.",
    S: "Good placement.",
    SSW: "Good placement.",
    SW: "Good placement.",
    WSW: "Good placement.",
    W: "Good placement.",
    WNW: "Still workable.",
    NW: "Still workable; suitable for light-weight storage or provisions.",
    NNW: "Diminishes intimacy and attraction.",
  },
};

// Room types in the order they appear in the report.
export const ROOM_TYPES = ["Kitchen", "Bedroom", "Toilet", "Temple", "Drawing Room", "TV / Mobile", "Store / Stair"];

// Rows for a room type's table: [{ code, name, element, effect }]
export function roomEffectRows(roomType) {
  const map = EFFECTS[roomType] || {};
  return DIRECTIONS.map((d) => ({ ...d, effect: map[d.code] || "—" }));
}

// Maps a plan marker type id (from lib/rooms.js) to the closest master-sheet category.
export const MARKER_CATEGORY = {
  kitchen: "Kitchen",
  master_bedroom: "Bedroom",
  bedroom: "Bedroom",
  guest: "Bedroom",
  pooja: "Temple",
  toilet: "Toilet",
  living: "Drawing Room",
  dining: "Drawing Room",
  staircase: "Store / Stair",
  store: "Store / Stair",
};

// Specific effect of a room category in a direction code; null when not defined.
export function getRoomEffect(category, dirCode) {
  const map = EFFECTS[category];
  if (!map) return null;
  const e = map[dirCode];
  return e && e !== "—" ? e : null;
}

// Element colour for an 8/16 direction code (falls back to grey).
export function directionColor(dirCode) {
  const d = DIR_META[dirCode];
  return d ? d.color : "#64748B";
}

// ---- 45 Devtas of the Vastu Purusha Mandala ----

export const BRAHMA = {
  name: "Brahma",
  zone: "Center (Brahmasthan)",
  significance: "The creator and source of cosmic balance; governs overall well-being. Keep open, clean and clutter-free.",
};

// 13 inner Devtas (Deva Vithi + Manushya Vithi), excluding Brahma.
export const INNER_DEVTAS = [
  { name: "Aryama", zone: "East", significance: "Social bonds, friendships and worldly connections." },
  { name: "Vivaswan", zone: "South", significance: "Brilliance, radiance and vital energy." },
  { name: "Mitra", zone: "West", significance: "Friendship, collaboration and longevity." },
  { name: "Prithvidhar", zone: "North", significance: "Stability, grounding and the holding of wealth." },
  { name: "Apaha", zone: "North-East", significance: "Purity, water and healing energy." },
  { name: "Apahavatsa", zone: "North-East", significance: "Immunity, protection and progeny." },
  { name: "Savita", zone: "South-East", significance: "Solar energy, vitality and transformation." },
  { name: "Savitra", zone: "South-East", significance: "Life-force, creation and inspiration." },
  { name: "Indra", zone: "South-West", significance: "Authority, leadership and prosperity." },
  { name: "Indrajaya", zone: "South-West", significance: "Victory and success over rivals." },
  { name: "Rudra", zone: "North-West", significance: "Strength, change and dissolution." },
  { name: "Rajyakshma", zone: "North-West", significance: "Immunity and the elimination of disease." },
];

// 32 outer Devtas (Paishacha Vithi), 8 per cardinal side, in clockwise order from North-East.
export const OUTER_DEVTAS = [
  { name: "Aditi", zone: "NE", significance: "Boundless space, motherly protection and open-mindedness." },
  { name: "Diti", zone: "NE", significance: "Fulfilment of material desires and abundance." },
  { name: "Shikhi", zone: "E", significance: "Fire energy and vitality." },
  { name: "Parjanya", zone: "E", significance: "Fertility, fresh ideas and conception." },
  { name: "Jayant", zone: "E", significance: "Victory, growth and success." },
  { name: "Mahendra", zone: "E", significance: "Administrative power, authority and connections." },
  { name: "Surya", zone: "E", significance: "Fame, health, vigour and leadership." },
  { name: "Satya", zone: "E", significance: "Truth, integrity and reputation." },
  { name: "Brisha", zone: "SE", significance: "Intellect and strong, clear thinking." },
  { name: "Akash", zone: "SE", significance: "Space, clarity and expansion." },
  { name: "Anil", zone: "SE", significance: "Air, movement and restlessness." },
  { name: "Pusha", zone: "S", significance: "Nourishment; weak decision-making if disturbed." },
  { name: "Vitatha", zone: "S", significance: "Falsehood and illusion; needs balance." },
  { name: "Grihakshat", zone: "S", significance: "Protection and security of the home." },
  { name: "Yama", zone: "S", significance: "Discipline, law, dharma and justice." },
  { name: "Gandharva", zone: "S", significance: "Arts, music, charm and preservation of vitality." },
  { name: "Bhringraj", zone: "SW", significance: "Negativity and decay; must be controlled." },
  { name: "Mrigah", zone: "SW", significance: "Pursuits, search and exploration." },
  { name: "Pitra", zone: "SW", significance: "Ancestors, lineage and forefathers' blessings." },
  { name: "Dauwarik", zone: "W", significance: "Gatekeeping, access control and protection." },
  { name: "Sugreev", zone: "W", significance: "Guidance, counsel and learning." },
  { name: "Pushpadant", zone: "W", significance: "Flowering, prosperity and abundance." },
  { name: "Varun", zone: "W", significance: "Water, relationships and worldly success." },
  { name: "Asur", zone: "W", significance: "Material pursuits and worldly desires." },
  { name: "Shosha", zone: "NW", significance: "Drying and depletion; needs careful control." },
  { name: "Papayakshma", zone: "NW", significance: "Guilt and negativity; needs balance." },
  { name: "Roga", zone: "NW", significance: "Illness and afflictions; must be controlled." },
  { name: "Naga", zone: "N", significance: "Secrets, intrigue and sensual desires." },
  { name: "Mukhya", zone: "N", significance: "Wealth, resources and new opportunities." },
  { name: "Bhallat", zone: "N", significance: "Strength, gains and prosperity." },
  { name: "Soma", zone: "N", significance: "Wealth, health and prosperity (Kubera)." },
  { name: "Bhujag", zone: "N", significance: "Hidden matters, secrets and sensuality." },
];
